import styled, { css } from "styled-components";
import { colors, spacing } from "../../utils";
import { device } from "../../utils/device";
import { mq, BP } from "../../utils/breakpoints";
import { motion } from "framer-motion";
import {
  BUSINESS_BOX_LEFT_INSET_PX,
  BUSINESS_BOX_WIDTH_PX,
} from "../WheelGallery/BusinessBox/styles";
import { HEADER_SEARCH_BAR_LEFT_INSET_PX } from "../WheelGallery/Header/styles";

// The business box's own right edge, measured from the shared viewport-left
// origin both the header and <main> render from (pages/Gallery/index.jsx has
// no ancestor max-width/centering wrapper on either side — confirmed live).
const BUSINESS_BOX_RIGHT_EDGE_PX =
  BUSINESS_BOX_WIDTH_PX + BUSINESS_BOX_LEFT_INSET_PX;

// QA-149 #2 (Kelly/Gordon, 2026-08-20): the field's right edge was aligned to
// the business box's right edge (BUSINESS_BOX_RIGHT_EDGE_PX minus the header's
// own left inset). MEASURED live that landed the ceiling at 288px.
export const SEARCH_BAR_BUSINESS_BOX_ALIGNED_WIDTH_PX =
  BUSINESS_BOX_RIGHT_EDGE_PX - HEADER_SEARCH_BAR_LEFT_INSET_PX;

// gordon-0824-global-search-bar-spacing (Gordon 2026-08-25) — SUPERSEDES the
// strict business-box alignment above: "make the search box WIDER (for longer
// names)." MEASURED live on web-stg (main.185a846d.js): the 288px-aligned box
// left the results dropdown only 288px wide, crushing a user-result name column
// to ~35px and ellipsising surnames ("Kelly Ellerbrake" → "Kelly …"). Widen the
// ceiling by a defined legibility delta so the field — and the dropdown that
// inherits its width (ResultsContainer width:100%) — has room for a full name.
// The right edge now extends this delta PAST the business box on purpose; the
// name wrap-to-2-lines fix (UserItem.jsx) is the belt to this suspenders.
export const SEARCH_BAR_NAME_LEGIBILITY_WIDENING_PX = 96;
export const SEARCH_BAR_MAX_WIDTH_PX =
  SEARCH_BAR_BUSINESS_BOX_ALIGNED_WIDTH_PX +
  SEARCH_BAR_NAME_LEGIBILITY_WIDENING_PX;

const UNIFIED_ICON_CHROME = `
  border: none;
  background: none;
  box-shadow: none;
`;

const UNIFIED_INPUT_CHROME = `
  appearance: none;
  -webkit-appearance: none;
  background: transparent;
  box-shadow: none;
`;

// T22.232 (UX audit UX-32, MEASURED web-stg main.dd06f65e.js @390x844): the
// phone header's search field was whatever width the rest of its row left
// over -- 33px on /discover and /following (a lone caret), 90px on /settings,
// 96px on View Wheel ("Search i...") -- so it jumped size from page to page.
// In phone portrait the CLOSED field is now one fixed icon-only button on every
// header; tapping it focuses the input, which opens the existing full-screen
// search overlay ($mobileOverlayActive) where the placeholder and the typed
// query are legible.
export const PHONE_SEARCH_ICON_BUTTON_PX = 44;
export const PHONE_SEARCH_ICON_MEDIA = `(max-width: ${BP.phoneMax}px) and (orientation: portrait)`;

export const Container = styled.div`
  /* Positioned wrapper so the absolute ResultsContainer anchors to (and matches
     the width of) the field itself rather than a random positioned ancestor. */
  position: relative;
  min-width: 180px;
  width: 30vw;
  max-width: ${SEARCH_BAR_MAX_WIDTH_PX}px;

  @media (${device.mobilePortrait}) {
    min-width: 10px;
    width: 100%;
  }

  @media (${device.mobileLandscape}) {
    flex-grow: 1;
  }

  /* ISP-4018: the 180px floor above is orientation-scoped (the mobilePortrait
     override only lifts it in PORTRAIT), so a narrow LANDSCAPE viewport kept a
     180px search field and forced the header wider than the screen. Below the
     tablet-portrait cut the field must always be allowed to shrink; it never
     shrinks when there is room, because width/max-width still govern. */
  ${mq.tabletP} {
    min-width: 0;
  }

  /* search-comp-vY-01: while the mobile results overlay is open, THIS wrapper
     (not ResultsContainer) becomes the fixed full-viewport surface, laid out
     as a flex column with the query field (SearchContainer) as its first,
     always-visible child and the results panel filling the rest. Previously
     only ResultsContainer went position:fixed/z-index:10000, which floated it
     ABOVE its own sibling SearchContainer in paint order -- MEASURED: the
     still-focused, still-typed-into <input> (correct value, correct width)
     was completely occluded underneath the full-screen panel, so a phone user
     typed and could not see their own query or reach the clear (x). Scoped to
     $mobileOverlayActive (isMobilePortrait && results actually open) so every
     other state -- desktop, mobile with the dropdown closed -- is unaffected.
     Fixed positioning here is deliberately on Container, not ResultsContainer:
     Container is SearchBar's own root, so this is correct regardless of which
     header (GalleryHeader / MobileView SearchHeader / WheelHeader) mounts it,
     with no per-mount pixel offset required. */
  ${({ $mobileOverlayActive }) =>
    $mobileOverlayActive &&
    css`
      @media (${device.mobilePortrait}) {
        position: fixed;
        inset: 0;
        z-index: 10000;
        width: auto;
        max-width: none;
        display: flex;
        flex-direction: column;
        background-color: ${colors.white};
      }
    `}

  ${({ $mobileOverlayActive }) =>
    !$mobileOverlayActive &&
    css`
      @media ${PHONE_SEARCH_ICON_MEDIA} {
        flex: 0 0 ${PHONE_SEARCH_ICON_BUTTON_PX}px;
        width: ${PHONE_SEARCH_ICON_BUTTON_PX}px;
        min-width: ${PHONE_SEARCH_ICON_BUTTON_PX}px;
        max-width: ${PHONE_SEARCH_ICON_BUTTON_PX}px;

        ${SearchContainer} {
          padding: 0;
          cursor: pointer;
        }
        ${SearchIcon} {
          left: 0;
          right: 0;
          margin: auto;
        }
        ${ClearIcon} {
          display: none;
        }
        ${TextInput} {
          min-width: 0;
          padding: 0;
          cursor: pointer;
          color: transparent;
          caret-color: transparent;
        }
        ${TextInput}::placeholder {
          color: transparent;
        }
      }
    `}
`;

// Screen-reader-only announcement target for the results dropdown. Scoped to the
// SearchBar on purpose: the page's existing role="status" regions belong to the
// SPA RouteAnnouncer, and hijacking those to say "10 users found." would corrupt
// route announcements for every other screen.
// Standard clip-rect technique (same as Help.styled / FirstRunIntro.styled):
// removed from the visual layer WITHOUT display:none / visibility:hidden, both of
// which take a node out of the accessibility tree and would silence it entirely.
export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const SearchContainer = styled.div`
  display: flex;
  flex-direction: row;
  background-color: ${colors.white};
  border: 1px solid #ced4da;
  /* rs-0914-dev-search-target-under-32px-390: was padding: 5px 10px. A
     percentage height on TextInput resolves against THIS element's content
     box, i.e. border-box height minus this padding minus the border — so the
     old top/bottom 5px silently ate 10px off the real hit target regardless
     of the min-height:40px floor below (MEASURED live at 390px: container
     56x42.19, but the focusable <input> itself only 34x30.19 — under the
     32px minimum, and a tap in that padding gap does not focus the field).
     Horizontal-only padding + align-items:center (still centers the icons,
     which are absolutely positioned) lets TextInput's height:100% actually
     fill the container's real box, closing the gap between visible field
     and focusable hit target. */
  padding: 0 10px;
  border-radius: 8px;
  height: 5vh;
  /* Floor the viewport-relative 5vh height so a short window cannot collapse
     the field below a usable size (measured ~26px on short viewports, which
     clipped the 20px icons + text). This is a legibility floor, NOT a WCAG
     touch-target inflation — the web app is click/mouse-driven per the
     click-surface ruling; 40px matches this component's own mobileLandscape
     height for internal consistency and is inert on normal-height desktops. */
  min-height: 40px;
  align-items: center;
  position: relative;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  /* Visible keyboard-focus indicator (WCAG 2.4.7). The inner <input> has
     outline:none, so the focus ring lives on the bordered field container.
     ISP-9999 (gordon-0923-P1-REPEAT-kelly-field-focus-style-one-shared-
     border-only-app-wide) — this used to also paint a 3px soft purple halo
     via box-shadow (the QA-80 2026-08-04 "single ring" law, since tightened
     by Gordon 2026-09-23 to border-color ONLY, no glow, matching the Edit
     Wheel "Wheel Name" Froala reference field exactly). Removed the
     box-shadow; the border recolor alone is the ONE shared data-field focus
     effect now (see src/theme/tokens.js "dataFieldFocus"). */
  &:focus-within {
    border-color: var(--iy-field-focus-color, ${colors.PrimaryPurple500});
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  @media (${device.mobileLandscape}) {
    height: 40px;
    border-radius: 4px;
  }

  /* search-comp-vY-01: once Container becomes the fixed flex-column overlay
     (see Container's $mobileOverlayActive rule above), this field is its
     first flex item -- pin it to its own natural height instead of letting
     flexbox's default shrink squash it against the results panel below. */
  @media (${device.mobilePortrait}) {
    flex-shrink: 0;
  }
`;
export const SearchIcon = styled(motion.img)`
  position: absolute; /* Absolute positioning */
  left: 10px; /* Adjust based on your padding */
  /* Center vertically with auto-margins rather than translateY(-50%): framer
     sets an inline transform for the icon's scale/rotate animation, which
     overrode translateY(-50%) at rest, leaving the icon pinned at top:25% and
     drifting further above centre as the 5vh field grew taller. */
  top: 0;
  bottom: 0;
  margin-top: auto;
  margin-bottom: auto;
  width: 20px; /* Set desired size */
  height: 20px;
  object-fit: contain; /* Ensures the icon scales correctly */
  object-position: center;
  display: block; /* Removes any extra space below the image */
  /* stgqa-0927-P0-kelly-gate-a-r093014: MEASURED live on web-stg.iyuga.net
     390x844 -- this decorative, non-interactive icon is position:absolute
     (paints above the normal-flow TextInput per CSS stacking) and at the
     field's mobile-collapsed 32px width it covers nearly the whole box, so
     a real tap aimed at the icon (the only visible affordance) never
     reaches the input beneath it and never focuses it. No handler on this
     element does anything with a click, so let taps fall through to
     whatever it's decorating instead of eating them. */
  pointer-events: none;
  ${({ $unifyFieldBorder }) =>
    $unifyFieldBorder ? UNIFIED_ICON_CHROME : ""}
`;

/* fix-resp0919-touch-targets-below-32px-both-env: MEASURED live on
   web-dev.iyuga.net/discover (390x844 AND 768x1024, bundle main.778bf9f4.js)
   at a real 20x20 hit area (elementFromPoint walk from center) -- under the
   project's 32px click-surface floor. This is a plain <img> (motion.img), a
   replaced element: ::before/::after never render on replaced elements, so
   the ::after hit-area overlay used elsewhere (src/pages/Capture/styles.js)
   would silently do nothing here. Instead the box itself grows to 32px and
   `padding` + `object-fit: contain` keep the painted glyph visually 20px,
   centered inside it -- a real (not invisible-overlay) 32px hit area.
   `right` shifts from 10px to 4px (10 - the 6px padding) so the VISUAL glyph
   stays anchored at the same on-screen position as before; only the tappable
   box grows, symmetrically, around it. */
export const ClearIcon = styled(motion.img)`
  cursor: pointer;
  position: absolute;
  right: 4px;
  /* Center vertically with auto-margins (see SearchIcon): the clear (×) icon
     is also a framer motion.img whose inline animation transform overrode the
     old translateY(-50%), leaving it off-centre at top:25%. */
  top: 0;
  bottom: 0;
  margin-top: auto;
  margin-bottom: auto;
  width: 32px;
  height: 32px;
  padding: 6px;
  box-sizing: border-box;
  object-fit: contain;
  object-position: center;
  display: block;
`;

export const TextInput = styled.input`
  outline: none;
  border: none;
  width: 100%;
  /* fix-fe-5499-search-input-webkit-width-31px-0917: follow-up to the
     lane-fe-5499 height fix. At 390px this field is a flex item with
     flex-basis:auto and no min-width, so its rendered width falls back to
     Flexbox's "automatic minimum size" — the content-based minimum driven
     by padding-left:30px plus the UA stylesheet's implicit padding on the
     other three sides. MEASURED live on web-dev (main.4a51db5b.js,
     Chromium vs WebKit, iPhone 13 viewport): that implicit padding resolves
     to "1px 2px 1px 30px" on Chromium but "1px 1px 1px 30px" on WebKit —
     the engines round the sub-pixel remainder differently — landing the
     field at 32px (Chromium, right at the touch-target floor) vs 31px
     (WebKit, 1px under it). An explicit min-width removes the automatic
     calculation as the deciding factor so both engines floor at the same
     value instead of independently rounding an implicit one.
     33px (not 32px) leaves a 1px margin above the floor so a WebKit rounded
     value can't land back at 31. */
  min-width: 33px;
  ${({ $unifyFieldBorder }) =>
    $unifyFieldBorder ? UNIFIED_INPUT_CHROME : ""}
  /* D1 (a11y-i18n-mobileweb matrix): the parent SearchContainer carries a
     min-height:40px floor, but this <input> itself had no height rule and
     sat at its intrinsic content height (~26px) inside that taller box — an
     independent qa-validator critic measured the raw <input> at 80.11x26px
     even after the container fix, because SearchContainer's extra vertical
     space was just padding AROUND a short input, not a taller input; a tap
     in that padding does not focus the field (no click-through handler).
     height:100% makes the actual focusable/clickable element fill its
     parent's real box, so the visible ~40px field and the real hit target
     are the SAME size. */
  height: 100%;
  padding-left: 30px;
  /* slack-attendance-...-0de99e: MEASURED live at 390px — Container's
     mobilePortrait rule lets this field shrink to ~64px next to the header's
     4 icons, and with no overflow rule the browser's default clip left the
     "Search iYuga" placeholder hard-cut mid-word ("Sear", no ellipsis).
     overflow:hidden + text-overflow:ellipsis makes any width below the
     content's natural width truncate with "…" instead. */
  overflow: hidden;
  text-overflow: ellipsis;

  &::placeholder {
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
    font-family: "DM Sans", sans-serif;
    color: #212529;
  }

  /* QA-80 (Gordon, 2026-08-04 -- universal design law): "the ONLY indicator
     should be a single purple outline around the ENTIRE component's border."
     SearchContainer above already paints that ONE ring on :focus-within
     (border-color + a 3px purple halo). Without this opt-out,
     src/styles/focusRing.css's app-wide :focus-visible:focus-visible rule
     (specificity (0,2,0)) beats the plain "outline: none" above it (0,1,0)
     regardless of source order, and paints a SECOND purple outline directly
     on this <input> -- the same double-ring defect measured and fixed on
     the Capture search bar (src/pages/Capture/styles.js). Doubled
     pseudo-class to match that (0,2,0) specificity; styled-components
     injects after the static global sheet, so equal specificity then
     resolves by source order in this rule's favor. */
  &:focus-visible:focus-visible {
    outline: none;
    box-shadow: none;
  }
`;

export const ResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  /* Match the field's width (Container) instead of a fixed 30vw, which
     overhung the 320px-max field by ~170px on wide viewports. */
  width: 100%;
  background-color: ${colors.white};
  box-shadow: 0px 0px 9.3px 0px #3b3b3b1c;
  border-radius: 8px;
  // overflow-y: auto;
  // overflow-x: hidden;
  overflow: hidden;
  z-index: 10000;
  /* gordon-0824-global-search-bar-spacing (Gordon 2026-08-25): "fix the dropdown
     row left-margin/spacing so result rows align properly." The panel had an
     ASYMMETRIC horizontal inset (padding-left 20 vs padding-right 10), so every
     result row + the Users/Wheels tab strip sat pushed ~10px to the right of
     centre — MEASURED live on web-stg (main.185a846d.js): row left inset 25px
     vs right ~20px. Equal left/right padding here (paired with the ItemsContainer
     symmetry below) centres the rows within the panel. */
  padding-left: 10px;
  padding-right: 10px;
  max-height: 405px;
  padding-bottom: 5px;

  @media (${device.mobilePortrait}) {
    /* search-comp-vY-01: the full-viewport takeover now lives on the PARENT
       Container (see styles.js Container's $mobileOverlayActive rule) as a
       fixed flex column, with the reused SearchContainer query field as its
       first child and this panel filling the rest -- so the field stays
       visible/reachable above the results instead of the two competing for
       the same fixed top:0 layer (MEASURED: this panel's own old
       position:fixed + z-index:10000 painted directly over its sibling
       SearchContainer, which had no z-index of its own -- the still-focused,
       correctly-valued <input> was completely unclickable and invisible
       underneath the results panel). This is now a plain flex child: no
       independent fixed positioning, no per-mount pixel offset, same
       GS-H14 "let the panel actually fill the remaining height" fix (405px
       desktop cap released) but expressed as flex:1 of Container's real
       height instead of its own 100dvh. */
    position: static;
    width: 100%;
    flex: 1;
    min-height: 0;
    max-height: none;
    border-radius: 0;
  }

  @media (${device.mobileLandscape}) {
    /* Same viewport-clamp fix as mobilePortrait above (search-390): fixed +
       left/right:0 instead of absolute + left:0/width:100vw. */
    position: fixed;
    top: 60px;
    left: 0;
    right: 0;
    width: auto;
    border-radius: 0;
    padding-left: 60px;
  }
`;

export const TabsContainer = styled.div`
  position: sticky;
  top: 0;
  background-color: ${colors.white};
  z-index: 1001; /* Ensure it stays above the scrollable content */
`;

export const ItemsContainer = styled.div`
  flex: 1; /* Take up remaining space */
  min-width: 0;
  overflow-y: auto;
  overflow-x: hidden;
  /* gordon-0824-global-search-bar-spacing (Gordon 2026-08-25): symmetric inner
     inset (was padding-right 10 / padding-left 5). Combined with the symmetric
     ResultsContainer padding above, a result row's left inset now equals its
     right inset, so rows align properly instead of hugging the right. */
  padding-right: 5px;
  padding-left: 5px;

  scroll-behavior: smooth; /* Smooth scrolling experience */
`;

export const InfiniteLoader = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 0;
`;

export const ResultType = styled.div`
  margin-top: 5px;
  margin-bottom: 5px;
  min-width: 0;
`;

/** Fixed-size clip so WheelComponent’s SVG/portrait layers cannot overlap the title */
export const SEARCH_RESULT_WHEEL_SIZE_PX = 60;

// GS-7.7 (qa-validator round 3, F-R3-D): border-radius matches
// CardWallpaperBackground's own border-radius:4 exactly (WheelGallery/
// WheelListing/styles.js) -- that layer fills this container with inset:0,
// so per the matching-corner-rounding rule (inner = outer − inset, inset
// here is 0) the two radii must be equal or the layer's rounded corners
// leave four square un-painted slivers against this container's previously-
// square corners.
export const SearchItemMedia = styled.div`
  flex-shrink: 0;
  width: ${SEARCH_RESULT_WHEEL_SIZE_PX}px;
  height: ${SEARCH_RESULT_WHEEL_SIZE_PX}px;
  overflow: hidden;
  position: relative;
  border-radius: 4px;
`;

export const BottomView = styled.div`
  padding: 10px;
  border-top: 1px solid ${colors.lightGrey};
  margin-top: 10px;
  &:hover {
    background-color: #f0f0f0;
    cursor: pointer;
  }

  @media (${device.mobileLandscape}) {
    display: none;
  }

  @media (${device.mobilePortrait}) {
    display: none;
  }
`;

/* ISP-9999 (#965): row density matches the Chat side-pane row (pages/Chat/
   styles.js `Row`) — ${spacing.sm}px/${spacing.lg}px (8px/16px) padding
   instead of the old flat 10px, and a flush WhatsApp-style hover treatment
   (background tint only, see &:hover below) instead of the old lifted-card
   scale+box-shadow (searchMotion.js `hover`), which visually widened the row
   on hover and ate into the horizontal space the username needs. */
export const SearchItem = styled(motion.div)`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: ${spacing.sm}px ${spacing.lg}px;
  display: flex;
  /* A single horizontal row for both WheelItem and UserItem. User results lay out
     as name-left / actions-right (BUG-search-568021, Gordon 2026-08-12): avatar +
     name/handle on the left, the Follow/Message actions inline to the RIGHT of the
     name. The actions are two compact 44px icon buttons at their natural width
     (they do NOT stretch), so the name column (SearchItemDetails: flex:1,
     min-width:0) ellipsises instead of being crushed — the failure the old
     full-width two-button row caused before it was moved to its own line. */
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${colors.Highlight};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/** The Follow + Message actions of a user result, inline to the RIGHT of the name
 *  (BUG-search-568021). margin-left:auto pins them to the row's right edge while
 *  the name column (flex:1) takes the remaining width; flex-shrink:0 keeps the two
 *  natural-width icon buttons intact so they never squash. */
export const UserActionsInline = styled.div`
  flex-shrink: 0;
  margin-left: auto;
  /* BUG-chat-search-icons-cover-lastname (Kelly QA-146): a fully-ellipsised name
     column's "…" butted directly against the first action icon (0px between them),
     reading as the icons "covering" the last name. This reserved gap guarantees a
     breathing space between the name and the two icon buttons regardless of name
     length, on top of the existing name-shrinks / icons-don't (flex-shrink:0)
     invariant. padding-left keeps the gap INSIDE the actions cell so margin-left:
     auto still pins the cell to the row's right edge.
     ISP-9999 (#965): retightened from 12px to the 8px floor this value is
     pinned against (UserItem.iconsGap.test.jsx) — paired with the Follow/
     Message icon-to-icon gap itself shrinking (UserActions' iconGap prop,
     set to 4px from this row), the actions cell now claims less width overall
     so a longer username has more room before it has to ellipsise. */
  padding-left: 8px;
  display: flex;
  align-items: center;
`;

/** Lets text truncate/wrap inside narrow dropdowns (flex default min-width:auto clips overflow) */
export const SearchItemDetails = styled.div`
  /* ISP-9999 (#965): 14px avatar-to-name gap matches the Chat side-pane row's
     own avatar/body gap (pages/Chat/styles.js Row, gap: 14px). */
  margin-left: 14px;
  flex: 1 1 0%;
  min-width: 0;
`;

export const Loader = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
`;

// search-comp-vY-04: the "No X Found" state was a bare text row (no icon,
// no subtext) -- replaces that single line with the app's standard
// icon+heading+subtext empty-state layout (matches Table/index.jsx's
// NotFound treatment).
export const EmptyResults = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  text-align: center;
`;
